import * as React from 'react';
import { useCallback } from 'react';
import { UseFormReturn, useFieldArray, useWatch } from 'react-hook-form';

import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDown,
  MapPinnedIcon,
  Plus,
  Trash2,
} from 'lucide-react';

import { Button } from 'components/Button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/Collapsible';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { LoadSectionAccordionItem } from 'pages/LoadView/LoadInformation/Components';
import { MultiStopBetaTooltip } from 'pages/QuoteView/LoadBuilding/components/MultiStopBetaTooltip';
import { LoadStopsItemTypes } from 'types/Load';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { cn } from 'utils/shadcn';

import { PlsRouteStopForm } from './PlsRouteStopForm';
import { AvailableTabs } from './constants';
import {
  MAX_PLS_ROUTE_STOPS,
  createPlsIntermediateStop,
  isPlsDropoffStopType,
  isPlsMultiStopRoute,
  normalizePlsRouteStopTypes,
  plsRouteStopCollapsedSummary,
  plsRouteStopLabel,
  syncPlsRouteStopsToPickupConsignee,
} from './plsRouteStops';
import { PlsFormValues } from './types';

type PlsRouteSectionProps = {
  formMethods: UseFormReturn<PlsFormValues>;
  activeTabs: string[];
  disabled?: boolean;
};

export function PlsRouteSection({
  formMethods,
  activeTabs,
  disabled = false,
}: PlsRouteSectionProps) {
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = formMethods;
  const { fields, insert, remove, move } = useFieldArray({
    control,
    name: 'stops',
  });

  const stops = useWatch({ control, name: 'stops' }) ?? [];
  const [collapsedByFieldId, setCollapsedByFieldId] = React.useState<
    Record<string, boolean>
  >({});

  const isStopOpen = (fieldId: string) => collapsedByFieldId[fieldId] !== true;

  const setStopOpen = React.useCallback((fieldId: string, open: boolean) => {
    setCollapsedByFieldId((prev) => {
      if (open) {
        if (prev[fieldId] !== true) {
          return prev;
        }
        const { [fieldId]: _, ...rest } = prev;
        return rest;
      }
      if (prev[fieldId] === true) {
        return prev;
      }
      return { ...prev, [fieldId]: true };
    });
  }, []);

  React.useEffect(() => {
    setCollapsedByFieldId((prev) => {
      const fieldIds = new Set(fields.map((field) => field.id));
      let changed = false;
      const next: Record<string, boolean> = {};
      for (const [id, collapsed] of Object.entries(prev)) {
        if (fieldIds.has(id)) {
          next[id] = collapsed;
        } else {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [fields]);

  React.useEffect(() => {
    if (!Array.isArray(errors.stops)) {
      return;
    }

    const errorIndex = errors.stops.findIndex(Boolean);
    const fieldId = fields[errorIndex]?.id;
    if (fieldId) {
      setStopOpen(fieldId, true);
    }
  }, [errors.stops, fields, setStopOpen]);

  const syncNormalizedStops = useCallback(() => {
    const nextStops = normalizePlsRouteStopTypes(getValues('stops') ?? []);
    const synced = syncPlsRouteStopsToPickupConsignee(nextStops);
    setValue('stops', nextStops, { shouldDirty: true });
    setValue('pickup', synced.pickup, { shouldDirty: true });
    setValue('consignee', synced.consignee, { shouldDirty: true });
    setValue('moreThanTwoStops', isPlsMultiStopRoute(nextStops), {
      shouldDirty: true,
    });
  }, [getValues, setValue]);

  const handleAddStop = useCallback(() => {
    if (fields.length >= MAX_PLS_ROUTE_STOPS) {
      return;
    }

    const insertIndex = fields.length - 1;
    insert(insertIndex, createPlsIntermediateStop(insertIndex));
    syncNormalizedStops();
  }, [fields.length, insert, syncNormalizedStops]);

  const handleRemoveStop = useCallback(
    (index: number) => {
      if (fields.length <= 2 || index <= 0 || index >= fields.length - 1) {
        return;
      }

      remove(index);
      syncNormalizedStops();
    },
    [fields.length, remove, syncNormalizedStops]
  );

  const handleMoveStop = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (
        toIndex < 1 ||
        toIndex >= fields.length - 1 ||
        fromIndex < 1 ||
        fromIndex >= fields.length - 1
      ) {
        return;
      }

      move(fromIndex, toIndex);
      syncNormalizedStops();
    },
    [fields.length, move, syncNormalizedStops]
  );

  return (
    <LoadSectionAccordionItem
      label='Route'
      icon={<MapPinnedIcon className='h-6 w-6' strokeWidth={1} />}
      name={AvailableTabs.route}
      activeTabs={activeTabs}
    >
      <Flex direction='col' gap='md' className='w-full'>
        {fields.map((field, index) => {
          const routeStopList =
            stops.length === fields.length ? stops : (getValues('stops') ?? []);
          const title = plsRouteStopLabel(routeStopList, index);
          const summary = plsRouteStopCollapsedSummary(routeStopList[index]);
          const isOpen = isStopOpen(field.id);
          const isFirstStop = index === 0;
          const isLastStop = index === fields.length - 1;
          const isIntermediate = !isFirstStop && !isLastStop;
          const moveUpDisabled = disabled || !isIntermediate || index <= 1;
          const moveDownDisabled =
            disabled || !isIntermediate || index >= fields.length - 2;
          const removeDisabled =
            disabled || !isIntermediate || fields.length <= 2;

          let resolvedStopType = LoadStopsItemTypes.Pickup;
          if (isFirstStop) {
            resolvedStopType = LoadStopsItemTypes.Pickup;
          } else if (isLastStop) {
            resolvedStopType = LoadStopsItemTypes.Dropoff;
          } else if (isPlsDropoffStopType(routeStopList[index]?.stopType)) {
            resolvedStopType = LoadStopsItemTypes.Dropoff;
          }

          return (
            <Collapsible
              key={field.id}
              open={isOpen}
              onOpenChange={(open) => setStopOpen(field.id, open)}
              className='w-full min-w-0 rounded-md border border-neutral-200 p-3'
            >
              <Flex
                align='start'
                justify='between'
                className='w-full'
                gap='sm'
                wrap='none'
              >
                <CollapsibleTrigger asChild>
                  <Button
                    type='button'
                    variant='unstyled'
                    size='auto'
                    className='flex min-w-0 w-full flex-1 items-start justify-start gap-2 rounded-sm px-0 py-0 text-left font-normal hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300'
                    buttonNamePosthog={null}
                    aria-label={
                      isOpen ? `Collapse ${title}` : `Expand ${title}`
                    }
                  >
                    <ChevronDown
                      className={cn(
                        'mt-0.5 h-4 w-4 shrink-0 text-neutral-500 transition-transform duration-200',
                        !isOpen && '-rotate-90'
                      )}
                    />
                    <Flex direction='col' gap='none' className='min-w-0 flex-1'>
                      <Typography
                        variant='body-sm'
                        weight='semibold'
                        className='min-w-0 truncate'
                      >
                        {title}
                      </Typography>
                      {!isOpen && summary ? (
                        <Typography
                          variant='body-xs'
                          className='truncate text-neutral-500'
                        >
                          {summary}
                        </Typography>
                      ) : null}
                    </Flex>
                  </Button>
                </CollapsibleTrigger>
                {isIntermediate && !disabled ? (
                  <Flex
                    align='center'
                    gap='xs'
                    wrap='none'
                    className='shrink-0'
                  >
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      className='h-8 px-2'
                      buttonNamePosthog={null}
                      title={moveUpDisabled ? 'Cannot move pickup' : 'Move up'}
                      disabled={moveUpDisabled}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleMoveStop(index, index - 1);
                      }}
                    >
                      <ArrowUpIcon className='h-4 w-4' />
                    </Button>
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      className='h-8 px-2'
                      buttonNamePosthog={null}
                      title={
                        moveDownDisabled ? 'Cannot move consignee' : 'Move down'
                      }
                      disabled={moveDownDisabled}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleMoveStop(index, index + 1);
                      }}
                    >
                      <ArrowDownIcon className='h-4 w-4' />
                    </Button>
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      className='h-8 w-8 shrink-0 px-0 text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700 disabled:text-neutral-300'
                      buttonNamePosthog={null}
                      title={
                        removeDisabled
                          ? 'At least one pickup and one consignee are required'
                          : 'Remove stop'
                      }
                      disabled={removeDisabled}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleRemoveStop(index);
                      }}
                    >
                      <Trash2 className='h-4 w-4' />
                    </Button>
                  </Flex>
                ) : null}
              </Flex>

              <CollapsibleContent
                forceMount
                hidden={!isOpen}
                className='min-w-0 w-full pt-3'
              >
                <fieldset disabled={disabled} className='min-w-0 w-full'>
                  <PlsRouteStopForm
                    stopIndex={index}
                    stopType={resolvedStopType}
                    formMethods={formMethods}
                    allowStopActionSelect={isIntermediate}
                  />
                </fieldset>
              </CollapsibleContent>
            </Collapsible>
          );
        })}

        {!disabled && fields.length < MAX_PLS_ROUTE_STOPS && (
          <Flex justify='center' className='w-full mt-1'>
            <MultiStopBetaTooltip>
              <Button
                buttonNamePosthog={ButtonNamePosthog.QuickQuoteAddStop}
                type='button'
                variant='ghost'
                onClick={handleAddStop}
                className='w-fit text-xs text-neutral-800 pl-1.5 hover:border-neutral-700 hover:bg-neutral-100 h-8 px-2.5'
                size='sm'
              >
                <Plus className='h-4 w-4 mr-1' />
                Add Stop
              </Button>
            </MultiStopBetaTooltip>
          </Flex>
        )}
      </Flex>
    </LoadSectionAccordionItem>
  );
}
